import React, { useRef, useState } from 'react';
import { useEditorStore } from './editor-store';
import { Nullable } from 'src/types/nullable.type';
import { JourneyName } from './components/journey-name';
import { useLogoStore } from './logo/store';
import { useEditorNavigator } from '../use-editor-navigator.hook';
import {
  CloseStrongIcon,
  InsightsBoldNeueIcon,
  SparklesStrongIcon,
  PreviewStrongIcon,
  HiddenRegularIcon,
  DeathstarStrongIcon,
  FolderStrongIcon,
} from 'src/monet/icons';
import { HintChipButton } from './components/hint-chip-button';
import { NeueTooltip } from './components/tooltip';
import { IconButton } from 'src/common/icon-button';
import { useCmdkStore } from 'src/cmdk/store';
import { trackSegmentEvent } from 'src/utils/segment';
import { ColorSelectionControlButton } from './components/color-selection-control-panel';
import { isCmdOrCtrlKeyPressed } from './utils/keyboard';
import { generateJourneyShareUrl } from 'src/utils/journey';
import { useCurrentOrganization } from 'src/store/organization';
import { useEditorContext } from 'src/store/editor';
import { animated } from '@react-spring/web';
import { NeueSharePanelButton } from './share-panel/button';
import { TalkToJourneyButtonEditor } from './talk-to-journey/button-editor';
import { isReadonly } from './native-e-signature/utils';
import { ThemeSelectionControlPanel } from './components/theme/selection-control-panel';
import { StatusSelectionControlPanel } from './components/status/selection-control-panel';
import { functionNoop } from 'src/utils/function/noop';
import { AnnotationEditorWidget } from './annotation/editor-widget';
import { ChartBarIcon } from '@heroicons/react/outline';
import { NeuePlayerIndexingPanel } from './neue-player/indexing-panel';
import { JourneyAlias } from 'src/common/interfaces/journey/alias.interface';
import { FoldersControlPanel } from './components/folder/folders-control-panel';

interface Props {
  onClickContainer: () => void;
  enableCmdk: boolean;
}

export const EditorTopBar = ({ onClickContainer, enableCmdk }: Props) => {
  const dispatchUserEditorAction = useEditorStore((state) => state.dispatchUserEditorAction);
  const { navigateToJourneysList, navigateToInsights, navigateToAnalytics } = useEditorNavigator();

  const journey = useEditorContext((state) => state.journey);
  const aliasAnnotationNodeId = useEditorContext((state) => state.aliasAnnotationNodeId);
  const currentOrganization = useCurrentOrganization((state) => state.currentOrganization);
  const online = useEditorStore((state) => state.online);
  const journeyName = useEditorStore((state) => state.journeyName);
  const resetLogoStore = useLogoStore((state) => state.reset);
  const setCmdkOpen = useCmdkStore((state) => state.setOpen);
  const sections = useEditorStore((state) => state.layout.sections);
  const readonly = isReadonly(sections);
  const [isStatusEnabled, setIsStatusEnabled] = useState(journey.status === 'public');
  const [isFoldersEnabled, setIsFoldersEnabled] = useState(journey.is_folders_enabled || false);

  const themeControlPanelOpenRef = useRef(functionNoop);
  const statusControlPanelOpenRef = useRef(functionNoop);
  const foldersControlPanelOpenRef = useRef(functionNoop);

  const indexingMenuOpenRef = useRef(functionNoop);
  const indexingMenuCloseRef = useRef(functionNoop);

  const journeyShareUrl = React.useMemo(
    () => generateJourneyShareUrl(currentOrganization, journey),
    [currentOrganization, journey]
  );

  const setJourneyName = (name: string) => {
    dispatchUserEditorAction(
      {
        type: 'set-journey-name',
        name,
      },
      {
        undoable: true,
        syncable: true,
      }
    );
  };

  let activeAlias = journey.aliases.find((alias: JourneyAlias) => alias.slug === journey.slug);

  if (!activeAlias) {
    activeAlias = journey.aliases.find((alias: JourneyAlias) => alias.journey_id === journey.id);
  }

  const onJourneyNameChange = (name: Nullable<string>) => {
    if (name !== null) {
      setJourneyName(name);
    }
  };

  const onPreviewClick: React.MouseEventHandler = (e) => {
    if (isCmdOrCtrlKeyPressed(e)) {
      trackSegmentEvent('Editor Preview Clicked', {
        cmd_key: true,
      });
    } else {
      trackSegmentEvent('Editor Preview Clicked', {
        cmd_key: false,
      });
    }
    window.open(journeyShareUrl, '_blank', 'noopener noreferrer');
  };

  const onLeaveFeedbackClick = () => {
    window.Beacon('open');
    window.Beacon('navigate', '/ask/chat/')
  };

  return (
    <animated.div
      className='z-neue-top-bar transition-colors text-neue-journey-bg absolute top-0 left-0 right-0 m-6 h-8 flex flex-row place-content-between items-center'
      onClick={onClickContainer}
    >
      <JourneyName journeyName={journeyName} onJourneyNameChange={onJourneyNameChange} />
      <div className='flex gap-2 items-center'>
        <IconButton
          onClick={() => {
            resetLogoStore();
            navigateToJourneysList();
          }}
        >
          <CloseStrongIcon />
        </IconButton>
        <HintChipButton onClick={onLeaveFeedbackClick}>Get help</HintChipButton>
      </div>
      <div className='flex flex-row gap-2'>
        {!online && (
          <div
            className='w-8 h-8 flex items-center justify-center'
            title='No connection detected - we’ll keep trying to save your progress'
          >
            <svg
              className='text-bedrock-red'
              width='20'
              height='20'
              viewBox='0 0 20 20'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M14.5 8.99983C16.1813 9.12423 17.25 10.2867 17.25 11.9998C17.25 13.7947 15.7949 15.2498 14 15.2498L8.31594 15.2498M11.5 4.99982C10.9418 4.73026 10.6614 4.74983 10 4.74983C7.73368 4.74983 5.88168 6.52373 5.75672 8.75883C4.07542 8.88323 2.75 10.2867 2.75 11.9998C2.75 12.3487 2.80498 12.6848 2.90675 12.9998'
                stroke='currentColor'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
              <path
                d='M16.25 3.75L3.75 16.25'
                stroke='currentColor'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </div>
        )}
        {enableCmdk && (
          <NeueTooltip placement='bottom' tooltipContent='Journey Brain'>
            <IconButton disabled={readonly} classes='w-8 h-8' onClick={() => setCmdkOpen(!enableCmdk)}>
              <SparklesStrongIcon />
            </IconButton>
          </NeueTooltip>
        )}
        <div className='relative'>
          <NeueTooltip placement='bottom' tooltipContent='Status'>
            <IconButton disabled={readonly} classes='w-8 h-8' onClick={() => foldersControlPanelOpenRef.current()}>
              <FolderStrongIcon className='w-5 h-5' />
            </IconButton>
          </NeueTooltip>
          <FoldersControlPanel
            openRef={foldersControlPanelOpenRef}
            journey={journey}
            isEnabled={isFoldersEnabled}
            setIsEnabled={setIsFoldersEnabled}
          />
        </div>
        <div className='relative'>
          <NeueTooltip placement='bottom' tooltipContent='Status'>
            <IconButton disabled={readonly} classes='w-8 h-8' onClick={() => statusControlPanelOpenRef.current()}>
              {isStatusEnabled ? <PreviewStrongIcon /> : <HiddenRegularIcon className='w-5 h-5' />}
            </IconButton>
          </NeueTooltip>
          <StatusSelectionControlPanel
            openRef={statusControlPanelOpenRef}
            journey={journey}
            isStatusEnabled={isStatusEnabled}
            setIsStatusEnabled={setIsStatusEnabled}
          />
        </div>
        <NeueTooltip placement='bottom' tooltipContent='AI Assistant'>
          <IconButton
            classes='w-8 h-8 ai-assistant-journey'
            onClick={() => {
            }}
          >
            <SparklesStrongIcon />
          </IconButton>
        </NeueTooltip>
        <NeueTooltip placement='bottom' tooltipContent='Insights'>
          <IconButton
            classes='w-8 h-8 insights-journey microsite-onboarding-insights'
            onClick={() => {
              navigateToInsights();
              trackSegmentEvent('Editor Insights Clicked', {});
            }}
          >
            <InsightsBoldNeueIcon />
          </IconButton>
        </NeueTooltip>
        <NeueTooltip placement='bottom' tooltipContent='Analytics'>
          <IconButton
            classes='w-8 h-8'
            onClick={() => {
              navigateToAnalytics();
              trackSegmentEvent('Editor Analytics Clicked', {});
            }}
          >
            <ChartBarIcon className='w-4 h-4' />
          </IconButton>
        </NeueTooltip>
        <div className='relative'>
          <NeueTooltip placement='bottom' tooltipContent='Theme'>
            <ColorSelectionControlButton
              disabled={readonly}
              onClick={() => {
                themeControlPanelOpenRef.current();
              }}
            />
          </NeueTooltip>
          <ThemeSelectionControlPanel openRef={themeControlPanelOpenRef} />
        </div>
        {!!activeAlias && (
          <div className='relative'>
            <NeueTooltip placement='bottom' tooltipContent='Indexing'>
              <>
                <IconButton
                  classes='!w-8 !h-8'
                  onClick={() => {
                    indexingMenuOpenRef.current();
                  }}
                >
                  <DeathstarStrongIcon />
                </IconButton>
                <NeuePlayerIndexingPanel
                  openRef={indexingMenuOpenRef}
                  closeRef={indexingMenuCloseRef}
                  isEnabled={!activeAlias.disable_indexing}
                  uuid={activeAlias.uuid}
                />
              </>
            </NeueTooltip>
          </div>
        )}
        <NeueTooltip placement='bottom' tooltipContent='Preview'>
          <IconButton classes='w-8 h-8' onClick={onPreviewClick}>
            <svg width='32' height='32' viewBox='0 0 32 32' fill='none' xmlns='http://www.w3.org/2000/svg'>
              <path
                d='M21.6667 15.9998L11.25 10.7915L11.25 21.2082L21.6667 15.9998Z'
                stroke='currentColor'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </IconButton>
        </NeueTooltip>
        <AnnotationEditorWidget creator={journey.creator} aliasAnnotationNodeId={aliasAnnotationNodeId} />
        <TalkToJourneyButtonEditor journey={journey} />
        {journey && journey.social_sharing && <NeueSharePanelButton journey={journey} platform='editor' />}
      </div>
    </animated.div>
  );
};
